import { mergeClasses } from "@fluentui/react-components";
import _ from "lodash";
import { memo, useCallback, useRef, useState } from "react";
import { ClauseCard } from "../../components/ClauseCard";
import { ClauseDocumentInfo } from "../../components/ClauseDocumentInfo";
import { DocumentReader } from "../../components/DocumentReader";
import { useAppData } from "../../hooks";
import { IClause, IDocument } from "../../interfaces";
import { suggestionApi } from "../../services/suggestion";
import { trigramsApi } from "../../services/trigrams.ts";
import { ClauseCardMode } from "../../types/enums";
import { ClauseTab } from "../../types/enums/clauseTabs";
import { CommentApp } from "../CommentApp";
import { SelectableTabList } from "./components/SelectableTabList";
import { SimilarClauseRenderer } from "./components/SimilarClauseRenderer";
import { useStyles } from "./styles";

export type ClauseInfoAppProps = {
  appId: string;
};

export const ClauseInfoApp = memo(({ appId }: ClauseInfoAppProps) => {
  const styles = useStyles();

  const [updatedText, setUpdatedText] = useAppData<string>(appId, "updatedText");
  const [selectedTab, setSelectedTab] = useAppData<string>(appId, "selectedTab");
  const [clause] = useAppData<IClause>(appId, "clause");
  const [similarClauseRedline, setSimilarClauseRedline] = useAppData<boolean>(appId, "similarClauseRedline");

  const [workspaceId] = useAppData<string>(appId, "workspaceId");

  const [openDocumentViewer, setOpenDocumentViewer] = useState<boolean>(false);

  const documentViewerButtonRef = useRef<HTMLButtonElement>(null);
  const mountNodeRef = useRef<HTMLDivElement>(null);
  const documentViewerRef = useRef<{ clause?: IClause; document: IDocument } | undefined>(undefined);
  const excludeIds = clause.clause_id ? [clause.clause_id] : [];

  const { data: similarClausesData = [], isFetching: isSimilarClauseLoading } =
    suggestionApi.useSearchSimilarSuggestionsQuery({
      appId,
      searchText: updatedText,
      workspaceId,
      excludeIds: excludeIds,
    });

  const { data: unusualPhrases = {} } = trigramsApi.useGetTrigramsForAppQuery(appId, {
    selectFromResult: ({ data = [], ...rest }) => {
      const ids = similarClausesData.map((s) => s.clause_id);
      const suggestionsUnusualPhrases = data.filter((u) => ids.includes(u.id));
      return {
        ...rest,
        data: _.keyBy(suggestionsUnusualPhrases, "id"),
      };
    },
    skip: isSimilarClauseLoading || !similarClausesData.length,
  });

  trigramsApi.useFetchTrigramsForSimilarClausesQuery(
    {
      appId,
      clauses: similarClausesData,
      query: {
        appId,
        searchText: updatedText,
        workspaceId,
        excludeIds: excludeIds,
      },
    },
    { skip: similarClausesData.length <= 0, refetchOnReconnect: true }
  );

  const onOpenClauseDocument = useCallback((document: IDocument, viewClause?: IClause) => {
    documentViewerRef.current = { document, clause: viewClause };
    setOpenDocumentViewer(true);
    documentViewerButtonRef.current?.click();
  }, []);

  return (
    <div className={styles.container}>
      <div className={mergeClasses(styles.overlay, !openDocumentViewer ? styles.displayNone : "")}></div>
      <DocumentReader
        mountNode={mountNodeRef.current}
        document={documentViewerRef.current?.document!}
        textBlock={documentViewerRef.current?.clause?.clause_text}
        trigger={<button style={{ visibility: "hidden" }} ref={documentViewerButtonRef}></button>}
        onClose={() => setOpenDocumentViewer(false)}
      />
      <div className={styles.mainClauseContainer}>
        <ClauseCard
          mode={ClauseCardMode.DETAIL}
          clause={{ ...clause, clause_text: updatedText }}
          searchText={updatedText}
          showScroll={true}
          workspaceId={workspaceId}
        />
      </div>

      <SelectableTabList value={selectedTab} onChange={setSelectedTab} />
      <div className={styles.tabContent}>
        {selectedTab === ClauseTab.DOCUMENT_INFO && (
          <ClauseDocumentInfo clause={clause} onOpenDocument={(document) => onOpenClauseDocument(document, clause)} />
        )}
        {selectedTab === ClauseTab.SIMILAR_CLAUSES && (
          <SimilarClauseRenderer
            isLoading={isSimilarClauseLoading}
            similarClauses={similarClausesData}
            originalText={updatedText}
            shouldRedline={similarClauseRedline}
            unusualPhrases={unusualPhrases}
            onSimilarClauseRedline={(enabled) => setSimilarClauseRedline(enabled)}
            onOpenDocumentViewer={onOpenClauseDocument}
            workspaceId={workspaceId}
            appId={appId}
          />
        )}
        {selectedTab === ClauseTab.COMMENTS && <CommentApp clauseId={clause.clause_id} workspaceId={workspaceId} />}
      </div>
    </div>
  );
});
